/**
 * 新增 / 编辑 **单个字或词** 的表单（弹窗里用）。
 *
 * ## 两个必须小心的地方
 *
 * 1. **`lessonIds` 是"整组替换"**：后端 `PATCH .../hanzi/:id` 收到 `lessonIds`
 *    就把关联**整组换成**给的那一份。而**列表响应里没有这个字段**（见
 *    `HanziView.lessonIds`），所以：
 *      · 编辑时先看这一行有没有 `lessonIds`（从 `GET .../hanzi/:id/words` 或
 *        `GET .../words` 反查来的）；
 *      · **查不到就不显示课文勾选区，也不提交这个字段** ——
 *        绝不能默认成 `[]` 提交，那会把家长辛苦关联的课文**静默清空**。
 *
 * 2. **拼音留空 = 用内置字表自动生成**（新建时），而**编辑时留空 = 清除覆盖**。
 *    两者在后端都是"不写 `pinyin` 覆盖值"，语义一致，界面上也用同一句话说明。
 */
import { useEffect, useState } from 'react';
import { GRADES, gradeLabel } from '@fht/shared';
import { Button } from '../ui/Button';
import { Input, Select } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { cn } from '../../lib/utils';
import { semesterLabel } from '../../lib/api/dictation';
import type { HanziView, LessonView, WordView } from '../../lib/api/dictation';
import { lessonsUiEnabled } from '../../lib/dictationLessonsUi';

export type ItemKind = 'hanzi' | 'word';

export interface ItemFormSubmit {
  /** `charText` / `wordText`：新建时要，编辑时不可改（所以编辑态不提交它）。 */
  text?: string;
  pinyin: string;
  gradeCode: string | null;
  semester: string | null;
  category: string;
  note: string;
  /** `word` 才有：是否来自课本词语表。 */
  fromTextbook: boolean;
  /**
   * 关联课文：`undefined` = **不动**（不提交这个字段）；
   * 数组 = 整组替换。查询失败时界面会给出 `undefined`。
   */
  lessonIds?: string[];
}

export interface DictationItemFormProps {
  open: boolean;
  kind: ItemKind;
  /** 编辑时给（用来回填）。 */
  editing: HanziView | WordView | null;
  /** 新建时要写进的那一册的 id（「出版社 + 年级 + 学期」解析出来的；空串 = 还没确定）。 */
  textbookId: string;
  lessons: LessonView[] | null;
  submitting: boolean;
  error: string | null;
  /** 课文关联查不到时给一句说明（此时界面不显示勾选区）。 */
  lessonLinksUnknown: boolean;
  /**
   * 正在查"这一条现在关联了哪些课文"。
   *
   * ⚠️ 为什么要单独一个标志：列表响应里**没有** `lessonIds`，编辑时必须先查一次。
   *   查完之前如果让家长看到勾选区（全空），他会以为"这一条没关联课文"；
   *   更糟的是他可能在查完之前点保存 —— 见 `onSubmit` 的说明。
   *   所以查的过程里勾选区显示骨架、并且不提交关联字段。
   */
  lessonLinksLoading: boolean;
  onClose: () => void;
  onSubmit: (payload: ItemFormSubmit) => void;
}

/**
 * 汉字（`hanzi`）的"类别"在第五步之后是**表类型**：
 * "这个字在哪张表上"（识字表 / 写字表）。
 *
 * ⚠️ 界面上的这一项**只在新建时**能选（`PUT .../hanzi/:id/listings` 才是改收录的
 *    入口）；编辑时这一格显示当前的收录，并提示去"收录"那一栏改。
 */
const HANZI_LIST_TYPES = [
  { value: 'xiezi', label: '写字表（会写）' },
  { value: 'shizi', label: '识字表（会认）' },
];

const WORD_CATEGORIES = [
  { value: 'common', label: '常用词' },
  { value: 'phrase', label: '词语' },
  { value: 'idiom', label: '成语' },
];

const SEMESTERS = ['上', '下', '不分'];

/**
 * 一行三格（年级 / 学期 / 收录到哪张表）的**标签统一高度**。
 *
 * ## 为什么（2026-10-18：弹框里三格没对齐）
 *
 * 用户截图里，第三格的标签「收录到哪张表（新建时）」比另两个长、**换行成两行**，
 * 于是它下面的控件被推下去，三格的输入框**不在一条线上**、看起来也比另两个"高/宽"。
 *
 * 两件事一起做（只动排版）：
 *   1. 标签**缩短**成「收录到哪张表」（来源那句话仍由 `hint` 讲）；
 *   2. 三格的标签统一成 `flex min-h-[2.5rem] items-end`：按**两行高**固定，
 *      文字贴底 —— 将来谁把标签改长、或窄屏下真的折成两行，控件仍在同一条线上。
 *
 * ⚠️ `min-h-[2.5rem]` = 两行 `text-sm`（20px × 2）。改这个值要三格一起改（同一个常量）。
 * ⚠️ 只加在这一行：别的字段不传 `labelClassName`，视觉**一个字都不变**。
 */
const ALIGNED_LABEL_CLASS = 'flex min-h-[2.5rem] items-end';

export function DictationItemForm({
  open,
  kind,
  editing,
  textbookId,
  lessons,
  submitting,
  error,
  lessonLinksUnknown,
  lessonLinksLoading,
  onClose,
  onSubmit,
}: DictationItemFormProps) {
  const [text, setText] = useState('');
  const [pinyin, setPinyin] = useState('');
  const [gradeCode, setGradeCode] = useState('');
  const [semester, setSemester] = useState('');
  const [category, setCategory] = useState(kind === 'hanzi' ? 'xiezi' : 'phrase');
  const [fromTextbook, setFromTextbook] = useState(false);
  const [note, setNote] = useState('');
  const [selectedLessons, setSelectedLessons] = useState<string[]>([]);

  // 打开时回填（编辑）或清空（新建）
  useEffect(() => {
    if (!open) return;
    if (editing === null) {
      setText('');
      setPinyin('');
      setGradeCode('');
      setSemester('');
      setCategory(kind === 'hanzi' ? 'xiezi' : 'phrase');
      setFromTextbook(false);
      setNote('');
      setSelectedLessons([]);
      return;
    }
    setText(kind === 'hanzi' ? (editing as HanziView).charText : (editing as WordView).wordText);
    // ⚠️ 不回填"生效值"：输入框里的值是**覆盖值**，留空 = 用自动值。
    //    直接把 pinyin 填进去会让"恢复自动"变成"再保存一次同样的覆盖"。
    setPinyin(editing.pinyinOverridden ? editing.pinyin : '');
    setGradeCode(editing.gradeCode ?? '');
    setSemester(editing.semester ?? '');
    /**
     * 字：显示**第一条收录的表类型**（0 条收录时回落到写字表 —— 那是"没收录"，
     * 由下面的提示说明；编辑时这一格本来就不提交）。
     * 词：常规的词语类别。
     */
    setCategory(
      kind === 'hanzi'
        ? ((editing as HanziView).listings?.[0]?.listType ?? 'xiezi')
        : (editing as WordView).category,
    );
    setFromTextbook(kind === 'word' ? (editing as WordView).fromTextbook : false);
    setNote(editing.note);
    setSelectedLessons(editing.lessonIds ?? []);
    // `editing` 的身份每次渲染都可能变（父组件新建对象），所以只依赖 open 与 id
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.id, kind]);

  /**
   * 课文关联是**异步查来的**（列表响应里没有这个字段），查到了再填勾选框。
   * ⚠️ 不能把它并进上面那个 effect 的依赖：那个 effect 只该在"换了一条记录"时跑。
   */
  useEffect(() => {
    if (!open) return;
    if (editing === null) return;
    if (lessonLinksLoading || lessonLinksUnknown) return;
    setSelectedLessons(editing.lessonIds ?? []);
  }, [open, editing, lessonLinksLoading, lessonLinksUnknown]);

  const canSubmit = kind === 'hanzi' ? [...text].length === 1 : text.trim() !== '';

  /**
   * 关联课文能不能动：**只有查完了、而且查到了**才允许提交这个字段。
   *
   * ⚠️ 这是本表单最危险的一处。后端 `lessonIds` 是"整组替换"，
   *   而列表响应里没有这个字段 —— 如果"还没查完"或"没查到"时提交一个空数组，
   *   家长辛苦关联的课文会被**静默清空**（界面不报错、数据已经没了）。
   *   所以这里把 `lessonLinksLoading` 也算进"不确定"。
   *
   * ⚠️ 2026-10-17：课文界面暂时隐藏（`DICTATION_LESSONS_UI_ENABLED=false`）时，
   *   勾选区不渲染，`selectedLessons` 恒是 `[]` —— 这时**绝不能**提交它，
   *   否则"改个备注"就会把（看不见的）课文关联清空。所以开关也进这个判断。
   */
  const lessonLinksCertain = !lessonLinksLoading && !lessonLinksUnknown && lessonsUiEnabled();

  const submit = () => {
    const base: ItemFormSubmit = {
      pinyin,
      gradeCode: gradeCode === '' ? null : gradeCode,
      semester: semester === '' ? null : semester,
      category,
      note,
      fromTextbook,
    };
    const withText: ItemFormSubmit = editing === null ? { ...base, text: text.trim() } : base;
    // 关联课文：**只有确认查到了时才提交**（见 `lessonLinksCertain`）
    const payload: ItemFormSubmit = lessonLinksCertain
      ? { ...withText, lessonIds: selectedLessons }
      : withText;
    onSubmit(payload);
  };

  return (
    <Modal
      open={open}
      title={editing === null ? (kind === 'hanzi' ? '新增生字' : '新增词语') : kind === 'hanzi' ? '编辑生字' : '编辑词语'}
      description={
        editing === null
          ? '拼音留空就用内置字表的自动读音；遇到多音字可以在这里直接写对的那个。'
          : '字/词本身不能改（改了就是另一条记录，请删掉重建）；拼音留空 = 恢复自动读音。'
      }
      onClose={onClose}
      size="md"
      footer={
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" disabled={submitting} onClick={onClose}>
            取消
          </Button>
          <Button loading={submitting} disabled={!canSubmit} onClick={submit}>
            保存
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label={kind === 'hanzi' ? '汉字（一个字）' : '词语'}
            value={text}
            disabled={editing !== null}
            autoComplete="off"
            spellCheck={false}
            onChange={(event) => setText(event.target.value)}
            error={text !== '' && !canSubmit ? (kind === 'hanzi' ? '必须正好一个汉字' : '请填写词语') : null}
          />
          <Input
            label="拼音（留空 = 用自动读音）"
            value={pinyin}
            autoComplete="off"
            spellCheck={false}
            className="font-mono"
            placeholder={
              kind === 'hanzi'
                ? '例如 háng'
                : '整词拼音，音节之间空格，例如 yín háng'
            }
            onChange={(event) => setPinyin(event.target.value)}
          />
        </div>

        {/*
          ⚠️ 2026-10-18（用户截图）：这一行三格必须**水平对齐** ——
             三列等宽栅格（`sm:grid-cols-3`）+ 控件 `w-full` + 标签统一两行高
             （`ALIGNED_LABEL_CLASS`），所以标签长短不再把某一格的控件顶歪。
          ⚠️ 窄屏（手机）是 `grid-cols-1`：三格各自一整行、折行不横向溢出，
             标签仍是同一套高度，三格的控件左边缘与宽度一致。
          ⛔ 只改排版：字段、校验、保存逻辑、只读/禁用属性**一处都没动**。
        */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Select
            label="年级"
            labelClassName={ALIGNED_LABEL_CLASS}
            value={gradeCode}
            onChange={(event) => setGradeCode(event.target.value)}
          >
            <option value="">不指定</option>
            {GRADES.map((grade) => (
              <option key={grade.code} value={grade.code}>
                {gradeLabel(grade.code)}
              </option>
            ))}
          </Select>
          <Select
            label="学期"
            labelClassName={ALIGNED_LABEL_CLASS}
            value={semester}
            onChange={(event) => setSemester(event.target.value)}
          >
            <option value="">不指定</option>
            {SEMESTERS.map((value) => (
              <option key={value} value={value}>
                {semesterLabel(value)}
              </option>
            ))}
          </Select>
          <Select
            /*
              ⚠️ 标签从「收录到哪张表（新建时）」缩短成「收录到哪张表」：
                 括号里那句是"什么时候能改"的说明，已经由下面的 `hint` 讲清楚了
                 （编辑时这一格禁用 + hint 指向「收录」那一栏）。
                 标签一长就换行，三格控件就歪 —— 这是用户截图里的那个问题。
            */
            label={kind === 'hanzi' ? '收录到哪张表' : '类别'}
            labelClassName={ALIGNED_LABEL_CLASS}
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            disabled={kind === 'hanzi' && editing !== null}
            hint={
              kind === 'hanzi' && editing !== null
                ? '收录要改的话走「收录」那一栏（编辑时这里不改）'
                : undefined
            }
          >
            {(kind === 'hanzi' ? HANZI_LIST_TYPES : WORD_CATEGORIES).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        {/*
          收录情况（只有字有）：一个字**可以同时在两张表上** —— 这是第五步改造的
          核心，所以编辑一个字时必须把"它现在在哪几张表上"说出来。
          ⚠️ `listings === null` = **没拿到**（不是"一张表都不在"）。
        */}
        {kind === 'hanzi' && editing !== null && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
            <p className="font-medium">这个字现在被收录在：</p>
            {(editing as HanziView).listings === null ? (
              <p className="mt-1 text-amber-700">收录情况没拿到（不是"没被收录"）—— 请刷新重试。</p>
            ) : (editing as HanziView).listings!.length === 0 ? (
              <p className="mt-1 text-slate-500">还没有收录到任何一张表。</p>
            ) : (
              <ul className="mt-1 list-disc space-y-0.5 pl-5">
                {(editing as HanziView).listings!.map((listing) => (
                  <li key={listing.id}>
                    {gradeLabel(listing.gradeCode)} · {semesterLabel(listing.semester)} ·{' '}
                    {listing.listType === 'shizi' ? '识字表' : listing.listType === 'xiezi' ? '写字表' : listing.listType}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {kind === 'word' && (
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-slate-300"
              checked={fromTextbook}
              onChange={(event) => setFromTextbook(event.target.checked)}
            />
            来自课本后面的「词语表」
          </label>
        )}

        <Input
          label="备注（可选）"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          maxLength={200}
        />

        {/* ---------- 关联课文（暂时隐藏，见 `lib/dictationLessonsUi.ts`） ---------- */}
        {lessonsUiEnabled() && (
          <div>
            <p className="text-sm font-medium text-slate-700">关联课文</p>
            {lessonLinksLoading ? (
              <p className="mt-1 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">
                正在查这一条现在关联了哪些课文…
              </p>
            ) : lessonLinksUnknown ? (
              <p className="mt-1 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                没能查到这一条现在关联了哪些课文。为免把已有联关系悄悄清空，
                <strong>这次保存不会改动课文关联</strong>。请刷新页面后重试。
              </p>
            ) : lessons === null ? (
              <p className="mt-1 text-sm text-slate-500">课文列表没拿到，暂时不能改关联。</p>
            ) : lessons.length === 0 ? (
              <p className="mt-1 text-sm text-slate-500">
                这个课本下还没有课文。可以先在「浏览与编辑 → 课本与课文」里建一节课。
              </p>
            ) : (
              <div className="mt-1 max-h-48 space-y-1 overflow-y-auto rounded-xl border border-slate-200 p-2">
                {lessons.map((lesson) => {
                  const checked = selectedLessons.includes(lesson.id);
                  return (
                    <label
                      key={lesson.id}
                      className={cn(
                        'flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1 text-sm',
                        checked ? 'bg-brand-50 text-brand-800' : 'text-slate-700 hover:bg-slate-50',
                      )}
                    >
                      <input
                        type="checkbox"
                        className="h-4 w-4 shrink-0 rounded border-slate-300"
                        checked={checked}
                        onChange={(event) =>
                          setSelectedLessons((prev) =>
                            event.target.checked
                              ? [...prev, lesson.id]
                              : prev.filter((id) => id !== lesson.id),
                          )
                        }
                      />
                      <span className="min-w-0 flex-1 truncate">
                        {lesson.lessonLabel !== '' ? `${lesson.lessonLabel} ` : ''}
                        {lesson.title}
                      </span>
                      <span className="shrink-0 text-xs text-slate-400">
                        {gradeLabel(lesson.gradeCode)} · {semesterLabel(lesson.semester)}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            {lessonLinksCertain && (
              <p className="mt-1 text-xs text-slate-500">
                保存时会<strong>整组替换</strong>这一条的课文关联（取消勾选 = 解除关联，不会删掉字/词本身）。
              </p>
            )}
          </div>
        )}

        {textbookId === '' && editing === null && (
          <p className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            还没确定要写进哪一册 —— 请先在「浏览与编辑」里把出版社 + 年级 + 学期选好、点「查询」；
            或者先去导入一份字表（导入时可以顺手建出这一册）。
          </p>
        )}

        {error !== null && (
          <p role="alert" className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </p>
        )}
      </div>
    </Modal>
  );
}
